/**
 * Shared CSV/XLSX parse + write helpers, used by ImportModal + ExportButton.
 */
import Papa from "papaparse";
import * as XLSX from "xlsx";

/**
 * Parse a File (CSV or XLSX) into { headers, rows: object[] }.
 * Rows are keyed by the first sheet's header row.
 */
export async function parseFile(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv") || file.type === "text/csv") {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const rows = results.data.filter((r) => Object.values(r).some((v) => v !== "" && v != null));
          const headers = results.meta?.fields || (rows[0] ? Object.keys(rows[0]) : []);
          resolve({ headers, rows });
        },
        error: (e) => reject(e),
      });
    });
  }
  // XLSX
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
  const headers = rows[0] ? Object.keys(rows[0]) : [];
  return { headers, rows };
}

/** Quick best-guess auto-map: source header → target field (case-insensitive equality, then substring). */
export function autoMap(sourceHeaders, targetFields) {
  const lower = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const idx = targetFields.reduce((acc, f) => ({ ...acc, [lower(f)]: f }), {});
  const out = {};
  for (const src of sourceHeaders) {
    const key = lower(src);
    if (idx[key]) { out[src] = idx[key]; continue; }
    const match = targetFields.find((f) => lower(f).includes(key) || key.includes(lower(f)));
    if (match) out[src] = match;
  }
  return out;
}

/** Apply column mapping: returns row objects keyed by target field names. */
export function applyMapping(rows, mapping) {
  return rows.map((row) => {
    const out = {};
    for (const [srcHeader, targetField] of Object.entries(mapping)) {
      if (!targetField) continue;
      out[targetField] = row[srcHeader];
    }
    return out;
  });
}

/** Sample value preview from rows for a given source header (returns first non-empty). */
export function sampleValue(rows, header) {
  for (const r of rows.slice(0, 100)) {
    const v = r[header];
    if (v !== "" && v != null) return String(v);
  }
  return "";
}

/** Export an array of objects to CSV (string) or XLSX (Blob), then trigger download. */
export function downloadExport({ filename, rows, format = "csv", columns = null }) {
  if (!rows || rows.length === 0) {
    rows = [{}];
  }
  const headers = columns || Object.keys(rows[0]);
  if (format === "xlsx") {
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Export");
    XLSX.writeFile(wb, `${filename}.xlsx`);
    return;
  }
  // CSV
  const csv = Papa.unparse({ fields: headers, data: rows });
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
